import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { RateLimitWaitOptions } from '../auth/rate-limit';

/**
 * The UI half of `waitOutRateLimit` (`@flashka/shared/auth`), for both sign-in
 * surfaces: `options` goes into the call, `seconds` is the countdown to show
 * while it waits (0 when it isn't waiting).
 *
 * Owns the wait's lifetime too: leaving the screen aborts it, so a code is
 * never emailed, nor a session minted, for a sign-in the user walked away
 * from. `clear()` drops the countdown once the call has settled.
 */
export function useRateLimitWait() {
  const [seconds, setSeconds] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const controller = useRef<AbortController | null>(null);

  // Refs only, so the identities below can stay stable without listing it.
  function stopTimer() {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  }

  // Made per mount, not per render: StrictMode's mount → unmount → mount
  // would otherwise leave the one controller aborted for good.
  useEffect(() => {
    controller.current = new AbortController();
    return () => {
      stopTimer();
      controller.current?.abort();
    };
  }, []);

  // Stable across renders, so it can sit in a useCallback's deps. `signal` is
  // a getter: a call made through `options` reads the live controller then.
  const options = useMemo<RateLimitWaitOptions>(
    () => ({
      get signal() {
        return controller.current?.signal;
      },
      onWait(step: number) {
        stopTimer();
        setSeconds(step);
        timer.current = setInterval(() => {
          setSeconds((s) => {
            if (s <= 1) {
              stopTimer();
              return 0;
            }
            return s - 1;
          });
        }, 1000);
      },
    }),
    [],
  );

  const clear = useCallback(() => {
    stopTimer();
    setSeconds(0);
  }, []);

  return { seconds, options, clear };
}
